import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { authClient } from "@/lib/musicApi";
import { useTranslation } from "react-i18next";

const PENDING_KEY = "samtwo_ml_pending";

// Shared "enter the 6-digit code" widget for every magic-link waiting screen. The
// code is typed in the ORIGINAL requesting browser; the backend validates it against
// the pending record (not a strict cookie re-match), so clearing cookies mid-flow
// still works here. On success the parent's onSuccess(data) issues the session.
export const MagicCodeEntry = ({ pendingId, onSuccess }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [totp, setTotp] = useState("");

  // Mirror the pending id so a reload of the waiting screen keeps the code path.
  useEffect(() => {
    if (pendingId) {
      try { sessionStorage.setItem(PENDING_KEY, pendingId); } catch { /* noop */ }
    }
  }, [pendingId]);

  const field = {
    background: "var(--bg-subtle)",
    border: "1px solid var(--border-color)",
    color: "var(--text-primary)",
  };

  const errFor = (reason) => {
    if (reason === "expired") return t("auth.codeExpired");
    if (reason === "locked") return t("auth.codeTooMany");
    if (reason === "too_many") return t("auth.codeTooMany");
    return t("auth.codeInvalid");
  };

  const succeed = (data) => {
    try { sessionStorage.removeItem(PENDING_KEY); } catch { /* noop */ }
    onSuccess(data);
  };

  const verify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setBusy(true); setError("");
    try {
      const { data } = await authClient.post("/magic-link/verify-code", { pending_id: pendingId, code });
      if (data?.requires_2fa) { setTempToken(data.temp_token); setNeeds2fa(true); setBusy(false); return; }
      succeed(data);
    } catch (err) {
      setError(errFor(err?.response?.data?.detail?.reason));
      setBusy(false);
    }
  };

  const verify2fa = async (e) => {
    e.preventDefault();
    if (totp.length < 6) return;
    setBusy(true); setError("");
    try {
      const { data } = await authClient.post("/subscriber/login-2fa", { temp_token: tempToken, totp_code: totp });
      succeed(data);
    } catch (err) {
      setError(t("auth.codeInvalid"));
      setBusy(false);
    }
  };

  if (needs2fa) {
    return (
      <form onSubmit={verify2fa} data-testid="magic-2fa-form" className="mt-6 pt-5" style={{ borderTop: "1px solid var(--border-color)" }}>
        <p className="text-base font-semibold mb-1" style={{ color: "var(--text-primary)" }}>{t("auth.twofaTitle")}</p>
        <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>{t("auth.twofaPrompt")}</p>
        {error && <p data-testid="magic-2fa-error" className="text-sm mb-2" style={{ color: "#ef4444" }}>{error}</p>}
        <input
          inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={totp}
          onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
          data-testid="magic-2fa-input" placeholder={t("auth.codePlaceholder")}
          className="w-full px-4 py-3 rounded-lg text-sm outline-none mb-3 tracking-[0.3em] text-center" style={field} />
        <button type="submit" disabled={busy || totp.length < 6} data-testid="magic-2fa-submit"
          className="w-full px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
          style={{ background: "var(--accent-primary)", color: "#fff" }}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : null}{t("auth.verify")}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={verify} data-testid="magic-code-form" className="mt-6 pt-5 text-left" style={{ borderTop: "1px solid var(--border-color)" }}>
      <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>{t("auth.codeEntryHint")}</p>
      {error && <p data-testid="magic-code-error" className="text-sm mb-2" style={{ color: "#ef4444" }}>{error}</p>}
      <label className="block text-sm mb-1 font-semibold" style={{ color: "var(--text-secondary)" }}>{t("auth.codeLabel")}</label>
      <input
        inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        data-testid="magic-code-input" placeholder={t("auth.codePlaceholder")}
        className="w-full px-4 py-3 rounded-lg text-sm outline-none mb-3 tracking-[0.3em] text-center" style={field} />
      <button type="submit" disabled={busy || code.length !== 6} data-testid="magic-code-submit"
        className="w-full px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
        style={{ background: "var(--accent-primary)", color: "#fff" }}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : null}{busy ? t("auth.codeVerifying") : t("auth.codeVerify")}
      </button>
    </form>
  );
};

export const MAGIC_PENDING_KEY = PENDING_KEY;
export default MagicCodeEntry;
